'use client'

import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import styles from './styles.module.css'
import { getStrapiImageUrl } from '@/lib/strapi'

interface MediaImage {
  url?: string
  alternativeText?: string
  data?: {
    attributes?: {
      url?: string
      alternativeText?: string
    }
  }
}

interface GalleryItem {
  id?: number
  img_desktop?: MediaImage
  img_mobile?: MediaImage
  proportion?: 'two:one' | 'one:one'
}

interface GalleryData {
  gallery_items?: Array<GalleryItem>
}

interface Props {
  data: GalleryData
}

const extractImage = (media?: MediaImage): { url: string; alt: string } => {
  if (!media) return { url: '', alt: '' }

  if (media.data?.attributes?.url) {
    return {
      url: getStrapiImageUrl(media.data.attributes?.url),
      alt: media.data.attributes?.alternativeText || ''
    }
  }

  if (media.url && media.url.trim() !== '') {
    return {
      url: getStrapiImageUrl(media.url),
      alt: media.alternativeText || ''
    }
  }

  return { url: '', alt: '' }
}

const Gallery: React.FC<Props> = ({ data }) => {
  const items = data.gallery_items || []
  const [isMobile, setIsMobile] = useState(false)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 767)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    onSelect()
  }, [emblaApi, onSelect])

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index)
    },
    [emblaApi]
  )

  return (
    <div className={styles.sliderContainer}>
      <div className={styles.embla}>
        <div className={styles.embla__viewport} ref={emblaRef}>
          <div className={styles.embla__container}>
            {items.map((item, index) => {
              const desk = extractImage(item.img_desktop)
              const mob = extractImage(item.img_mobile)
              const img = isMobile ? (mob.url ? mob : desk) : desk

              if (!img.url) return null

              const isActive = index === selectedIndex

              const ratio =
                item.proportion === 'one:one'
                  ? '66.3%'
                  : '50%'

              return (
                <div
                  className={`${styles.embla__slide} ${
                    isActive ? styles.embla__slideActive : ''
                  }`}
                  key={item.id || index}
                >
                  <div
                    className={styles.imageWrapper}
                    style={{ paddingTop: ratio }}
                  >
                    <Image
                      src={img.url}
                      alt={img.alt}
                      fill
                      className={styles.sliderImage}
                    />
                  </div>

                  {/* Кнопки внутри активного слайда */}
                  {!isMobile && isActive && index > 0 && (
                    <button className={styles.slidePrev} onClick={scrollPrev}>
                      <Image
                        src="/images/portfolio/multibanking/slider-stages-button-left.svg"
                        alt="Previous"
                        width={50}
                        height={50}
                      />
                    </button>
                  )}

                  {!isMobile && isActive && index < items.length - 1 && (
                    <button className={styles.slideNext} onClick={scrollNext}>
                      <Image
                        src="/images/portfolio/multibanking/slider-stages-button-right.svg"
                        alt="Next"
                        width={50}
                        height={50}
                      />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className={styles.dots}>
        {items.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${selectedIndex === index ? styles.dotActive : ''}`}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </div>
  )
}

Gallery.displayName = 'Gallery'

export default Gallery
